import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaHome,
  FaChartLine,
  FaReceipt,
  FaChartPie,
  FaBarcode,
  FaPlus
} from 'react-icons/fa';
import './FloatingButtons.css';

const FloatingButtons = ({ 
  currentPage = 'dashboard',
  showDashboard = true,
  showAnalytics = true,
  showBarcode = true,
  showPaidAmounts = true,
  showPaidSummary = true,
  position = 'right',
  size = 'large'
}) => {
  const navigate = useNavigate();

  const buttonConfig = {
    dashboard: {
      path: '/dashboard/cashier',
      icon: FaHome,
      title: 'Back to Dashboard',
      color: 'primary',
      size: size === 'large' ? 80 : 60,
      iconSize: size === 'large' ? 28 : 20
    },
    analytics: {
      path: '/dashboard/cashier/analytics',
      icon: FaChartLine,
      title: 'Analytics Dashboard',
      color: 'warning',
      size: size === 'large' ? 80 : 60,
      iconSize: size === 'large' ? 28 : 20
    },
    barcode: {
      path: '/collection/barcode-payment',
      icon: FaBarcode,
      title: 'Barcode Payment',
      color: 'info',
      size: size === 'large' ? 90 : 70,
      iconSize: size === 'large' ? 32 : 24,
      isPrimary: true // Main action button
    },
    paidAmounts: {
      path: '/collection/paid-amounts',
      icon: FaReceipt,
      title: 'Paid Amounts Detail',
      color: 'success',
      size: size === 'large' ? 80 : 60,
      iconSize: size === 'large' ? 28 : 20
    },
    paidSummary: {
      path: '/collection/paid-summary',
      icon: FaChartPie,
      title: 'Payment Summary',
      color: 'secondary',
      size: size === 'large' ? 80 : 60,
      iconSize: size === 'large' ? 28 : 20
    }
  };

  const colorStyles = {
    primary: {
      background: 'linear-gradient(135deg, #007bff, #0056b3)',
      shadowColor: '0, 123, 255'
    },
    warning: {
      background: 'linear-gradient(135deg, #ffc107, #e0a800)',
      shadowColor: '255, 193, 7'
    },
    info: {
      background: 'linear-gradient(135deg, #17a2b8, #138496)',
      shadowColor: '23, 162, 184'
    },
    success: {
      background: 'linear-gradient(135deg, #28a745, #1e7e34)',
      shadowColor: '40, 167, 69'
    },
    secondary: {
      background: 'linear-gradient(135deg, #6c757d, #545b62)',
      shadowColor: '108, 117, 125'
    }
  };

  const getButtonsToShow = () => {
    const buttons = [];

    console.log('FloatingButtons Debug:', { 
      currentPage, 
      showDashboard, 
      showAnalytics, 
      showBarcode, 
      showPaidAmounts, 
      showPaidSummary 
    });

    // Always show dashboard unless it's the current page
    if (showDashboard && currentPage !== 'dashboard') {
      buttons.push('dashboard');
    }

    // Show analytics unless it's the current page
    if (showAnalytics && currentPage !== 'analytics') {
      buttons.push('analytics');
    }

    // Always show barcode payment (most important)
    if (showBarcode && currentPage !== 'barcode') {
      buttons.push('barcode');
    }

    // Show paid summary unless it's the current page
    if (showPaidSummary && currentPage !== 'paidSummary') {
      buttons.push('paidSummary');
    }

    // Show paid amounts unless it's the current page
    if (showPaidAmounts && currentPage !== 'paidAmounts') {
      buttons.push('paidAmounts');
    }

    console.log('FloatingButtons to show:', buttons);
    return buttons;
  };

  const buttonsToShow = getButtonsToShow();

  console.log('FloatingButtons render check:', { buttonsToShow, length: buttonsToShow.length });

  if (buttonsToShow.length === 0) {
    // Show a debug indicator when no buttons are shown
    return (
      <div className={`floating-buttons floating-buttons-${position} floating-buttons-${size}`} style={{
        position: 'fixed',
        bottom: '30px',
        right: '30px',
        zIndex: 1000
      }}>
        <div style={{
          background: 'red',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px'
        }}>
          No Buttons (Page: {currentPage})
        </div>
      </div>
    );
  }

  const handleButtonClick = (path) => {
    navigate(path);
  };

  const renderButton = (buttonKey) => {
    const config = buttonConfig[buttonKey];
    const style = colorStyles[config.color];
    const IconComponent = config.icon;

    return (
      <button
        key={buttonKey}
        className={`floating-btn floating-btn-${config.color} ${config.isPrimary ? 'floating-btn-primary-action' : ''}`}
        onClick={() => handleButtonClick(config.path)}
        style={{
          width: `${config.size}px`,
          height: `${config.size}px`,
          borderRadius: '50%',
          border: 'none',
          background: style.background,
          boxShadow: `0 8px 25px rgba(${style.shadowColor}, 0.4)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          animation: config.isPrimary ? 'pulse 2s infinite' : 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow = `0 12px 35px rgba(${style.shadowColor}, 0.6)`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = `0 8px 25px rgba(${style.shadowColor}, 0.4)`;
        }}
        title={config.title}
      >
        <IconComponent size={config.iconSize} color="white" />
      </button>
    );
  };

  return (
    <div className={`floating-buttons floating-buttons-${position} floating-buttons-${size}`}>
      {buttonsToShow.map(renderButton)}
    </div>
  );
};

export default FloatingButtons;